// A link the assistant offers, and the URL it becomes.
//
// STORED AS INTENT, NEVER AS A URL (spec ruling 2). A link names a destination
// and its params; the href is built when someone clicks, from today's catalog
// and the conversation's own site. So a route renamed next month does not
// strand a link written this month, and a link can never point at another site:
// there is no site in it to point with.

import { acceptValue, decodeValue, encodeValue } from './params.js';
import {
  DESTINATIONS,
  isDestinationId,
  type DestinationId,
  type DestinationParams,
} from './destinations.js';

export interface AiLink {
  destination: DestinationId;
  params: Record<string, unknown>;
  /**
   * The names of what the link narrows to — a record's title, a floor — as the
   * tool that read them had them. Data, never the model's words: a label the
   * model wrote could promise what the link does not open.
   */
  caption?: string;
}

/** Long enough to recognise a record by, short enough to sit in a chip. */
export const MAX_CAPTION = 120;

function captionOf(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const caption = raw.replace(/\s+/g, ' ').trim();
  if (caption === '') return undefined;
  return caption.length <= MAX_CAPTION ? caption : `${caption.slice(0, MAX_CAPTION - 1)}…`;
}

/**
 * The params this destination accepts, and the names of the ones it did not.
 *
 * Lenient by design, and the caller decides what a drop means: a tool preparing
 * a link treats any drop as "the page cannot show this" and prepares nothing,
 * while a reader of a stored link opens the page without what no longer parses.
 */
export function cleanParams(
  destination: DestinationId,
  raw: unknown,
): { params: Record<string, unknown>; dropped: string[] } {
  const specs = DESTINATIONS[destination].params as Record<string, Parameters<typeof acceptValue>[0]>;
  const params: Record<string, unknown> = {};
  const dropped: string[] = [];
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return { params, dropped };
  for (const [name, value] of Object.entries(raw)) {
    const spec = Object.hasOwn(specs, name) ? specs[name] : undefined;
    const accepted = spec === undefined ? undefined : acceptValue(spec, value);
    if (accepted === undefined) dropped.push(name);
    else params[name] = accepted;
  }
  return { params, dropped };
}

/**
 * A link to `destination`. The params are NOT checked here: a tool builds them
 * from runtime values, and whether the result is exact is `isExactLink`'s call.
 */
export function linkTo(destination: DestinationId, params: Record<string, unknown>, caption?: string): AiLink {
  const kept = captionOf(caption);
  return { destination, params: { ...params }, ...(kept === undefined ? {} : { caption: kept }) };
}

/** True when every param is one the destination accepts, as given. */
export function isExactLink(link: AiLink): boolean {
  return cleanParams(link.destination, link.params).dropped.length === 0;
}

/**
 * A stored or streamed link, as far as today's catalog can still read it: an
 * unknown destination is null, and params that no longer parse are dropped.
 */
export function readLink(raw: unknown): AiLink | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const { destination, params, caption } = raw as Record<string, unknown>;
  if (!isDestinationId(destination)) return null;
  return linkTo(destination, cleanParams(destination, params).params, captionOf(caption));
}

/** The in-app path a link opens on `siteId`. */
export function hrefOf(siteId: string, link: Pick<AiLink, 'destination' | 'params'>): string {
  const { path, params: specs } = DESTINATIONS[link.destination];
  const { params } = cleanParams(link.destination, link.params);
  const inPath = new Set<string>();
  const pathname = path.replace(/\/:(\w+)\??/g, (_segment, name: string) => {
    if (name === 'siteId') return `/${encodeURIComponent(siteId)}`;
    inPath.add(name);
    const value = params[name];
    return value === undefined ? '' : `/${encodeURIComponent(value as string)}`;
  });
  const query = new URLSearchParams();
  // In declaration order, so one link has one href — which is what `linkKey` counts on.
  for (const [name, spec] of Object.entries(specs as Record<string, Parameters<typeof encodeValue>[0]>)) {
    if (inPath.has(name) || params[name] === undefined) continue;
    query.set(name, encodeValue(spec, params[name]));
  }
  const search = query.toString();
  return search === '' ? pathname : `${pathname}?${search}`;
}

/** One key per place a link opens, whatever order its params were given in. */
export function linkKey(link: Pick<AiLink, 'destination' | 'params'>): string {
  return hrefOf('', link);
}

/** A destination's params as they arrived in a page's URL, typed and cleaned. */
export function readParams<D extends DestinationId>(destination: D, search: URLSearchParams): DestinationParams<D> {
  const specs = DESTINATIONS[destination].params as Record<string, Parameters<typeof decodeValue>[0]>;
  const raw: Record<string, unknown> = {};
  for (const [name, spec] of Object.entries(specs)) {
    const value = search.get(name);
    if (value !== null) raw[name] = decodeValue(spec, value);
  }
  return cleanParams(destination, raw).params as DestinationParams<D>;
}
